import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import Popup from '@atlaskit/popup';
import { Box, Flex, Pressable, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import { ClassificationView } from '../..//common/ui/classification-view';
import { colorsConfig, disabledColorConfig, nameMaxLength } from '../../common/constants';
import { ClassificationIcon } from '../../common/ui/classification-icon';
import { messages } from '../../messages';

import { type DataClassificationLevelProps } from './types';

const styles = {
	base: xcss({
		display: 'inline-flex',
		gap: 'space.050',
		alignItems: 'center',
		justifyContent: 'center',
		whiteSpace: 'normal',
		textOverflow: 'ellipsis',
		cursor: 'default',
		borderRadius: 'border.radius',
	}),
	compact: xcss({
		height: '20px',
	}),
	expanded: xcss({
		height: '24px',
	}),
	disabled: xcss({
		color: 'color.text.disabled',
		backgroundColor: 'color.background.disabled',
		cursor: 'default',
	}),
	clickable: xcss({
		cursor: 'pointer',
	}),
	red: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.red.subtler',
	}),
	redClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.red.subtler',
		},
		':hover': {
			backgroundColor: 'color.background.accent.red.subtler.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.red.subtler.pressed',
		},
	}),
	redBold: xcss({
		color: 'color.text.inverse',
		backgroundColor: 'color.background.accent.red.bolder',
	}),
	redBoldClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.red.bolder',
		},
		':hover': {
			backgroundColor: 'color.background.accent.red.bolder.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.red.bolder.pressed',
		},
	}),
	orange: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.orange.subtle',
	}),
	orangeClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.orange.subtle',
		},
		':hover': {
			backgroundColor: 'color.background.accent.orange.subtle.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.orange.subtle.pressed',
		},
	}),
	yellow: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.yellow.subtlest',
	}),
	yellowClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.yellow.subtlest',
		},
		':hover': {
			backgroundColor: 'color.background.accent.yellow.subtlest.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.yellow.subtlest.pressed',
		},
	}),
	green: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.green.subtlest',
	}),
	greenClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.green.subtlest',
		},
		':hover': {
			backgroundColor: 'color.background.accent.green.subtlest.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.green.subtlest.pressed',
		},
	}),
	blue: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.blue.subtler',
	}),
	blueClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.blue.subtler',
		},
		':hover': {
			backgroundColor: 'color.background.accent.blue.subtler.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.blue.subtler.pressed',
		},
	}),
	teal: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.teal.subtler',
	}),
	tealClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.teal.subtler',
		},
		':hover': {
			backgroundColor: 'color.background.accent.teal.subtler.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.teal.subtler.pressed',
		},
	}),
	lime: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.lime.subtler',
	}),
	limeClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.lime.subtler',
		},
		':hover': {
			backgroundColor: 'color.background.accent.lime.subtler.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.lime.subtler.pressed',
		},
	}),
	purple: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.purple.subtlest',
	}),
	purpleClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.purple.subtlest',
		},
		':hover': {
			backgroundColor: 'color.background.accent.purple.subtlest.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.purple.subtlest.pressed',
		},
	}),
	grey: xcss({
		color: 'color.text',
		backgroundColor: 'color.background.accent.gray.subtlest',
	}),
	greyClickable: xcss({
		':focus-visible': {
			backgroundColor: 'color.background.accent.gray.subtlest',
		},
		':hover': {
			backgroundColor: 'color.background.accent.gray.subtlest.hovered',
		},
		':active': {
			backgroundColor: 'color.background.accent.gray.subtlest.pressed',
		},
	}),
	popup: xcss({
		width: '330px',
	}),
};

export default function DataClassificationLevel({
	testId,
	name,
	tooltip,
	tooltipPosition,
	color = 'GREY',
	iconSize = 'small',
	guideline,
	isDisabled = false,
	isPopupDisabled = false,
	isPopupOpenByDefault = false,
	getPopupStateControllers,
	popupZIndex,
	guidelineRepresentation = 'PLAIN',
	onPopupShown,
	onPopupClose,
	onClick,
	renderPopupComponent,
	popupLabel,
	popupTitleId,
	popupRole,
	popupPlacement = 'bottom-start',
	shouldRenderToParent,
	isIconOnly: isIconOnlyProp = false,
	associatedPoliciesCount,
	spacing = 'default',
}: DataClassificationLevelProps) {
	const { formatMessage } = useIntl();
	const [isPopupOpen, setIsPopupOpen] = useState(
		isDisabled || isPopupDisabled ? false : isPopupOpenByDefault,
	);
	if (getPopupStateControllers) {
		const getOpenState = () => isPopupOpen;
		const setOpenState = (isOpen: boolean) => setIsPopupOpen(isOpen);
		getPopupStateControllers(getOpenState, setOpenState);
	}

	const isMissingName = !name || name.length === 0;
	const isIconOnly = isMissingName || isIconOnlyProp;

	const nameWithRules = isMissingName
		? ''
		: associatedPoliciesCount
			? name + ' ' + formatMessage(messages.rules, { associatedPoliciesCount })
			: name;
	const nameTruncated = (nameWithRules ?? '').substring(0, nameMaxLength);

	const iconPx: number =
		iconSize === 'small'
			? 12
			: iconSize === 'medium'
				? 18
				: iconSize === 'large'
					? 24
					: iconSize === 'xlarge'
						? 32
						: iconSize === 'xxlarge'
							? 40
							: 12;

	const handleContainerClicked = (): void => {
		if (isPopupDisabled || isDisabled || isMissingName) {
			return;
		}

		if (onPopupShown) {
			onPopupShown();
		}

		setIsPopupOpen(!isPopupOpen);
	};

	const closePopup = () => {
		setIsPopupOpen(false);
	};

	const isClickable = !isPopupDisabled && !isDisabled && !isMissingName;

	const getContainer = (triggerProps = {}) => {
		// @ts-ignore
		const { 'aria-expanded': ariaExpanded, ...restProps } = triggerProps;

		const Container = isClickable ? Pressable : Box;

		return (
			<Container
				role={isClickable ? 'button' : 'presentation'}
				aria-disabled={isDisabled}
				xcss={[
					styles.base,
					// Add size styles if not icon only
					...(!isIconOnly ? [spacing === 'expanded' ? styles.expanded : styles.compact] : []),
					// Add disabled styles if disabled
					...(isDisabled ? [styles.disabled] : []),
					// Add color styles
					...(!isDisabled
						? [
								{
									RED: styles.red,
									RED_BOLD: styles.redBold,
									ORANGE: styles.orange,
									YELLOW: styles.yellow,
									GREEN: styles.green,
									BLUE: styles.blue,
									TEAL: styles.teal,
									LIME: styles.lime,
									PURPLE: styles.purple,
									GREY: styles.grey,
								}[color],
							]
						: []),
					// Add clickable styles if clickable
					...(!isDisabled && isClickable
						? [
								styles.clickable,
								{
									RED: styles.redClickable,
									RED_BOLD: styles.redBoldClickable,
									ORANGE: styles.orangeClickable,
									YELLOW: styles.yellowClickable,
									GREEN: styles.greenClickable,
									BLUE: styles.blueClickable,
									TEAL: styles.tealClickable,
									LIME: styles.limeClickable,
									PURPLE: styles.purpleClickable,
									GREY: styles.greyClickable,
								}[color],
							]
						: []),
				]}
				testId={testId}
				padding="space.050"
				onClick={() => {
					if (onClick) {
						onClick();
					}
					handleContainerClicked();
				}}
				{...(isClickable ? triggerProps : restProps)}
			>
				<Flex justifyContent="center" alignItems="center">
					<ClassificationIcon
						width={iconPx}
						height={iconPx}
						color={isDisabled ? disabledColorConfig.iconColor : colorsConfig[color].iconColor}
					/>
				</Flex>
				{!isIconOnly && (
					<Text
						size="UNSAFE_small"
						maxLines={1}
						color={color === 'RED_BOLD' ? 'color.text.inverse' : 'color.text'}
						testId="data-classification-level-name"
					>
						{nameTruncated}
					</Text>
				)}
			</Container>
		);
	};

	return isPopupDisabled ? (
		getContainer()
	) : (
		<Popup
			label={popupLabel}
			titleId={popupTitleId}
			role={popupRole}
			shouldRenderToParent={shouldRenderToParent}
			placement={popupPlacement}
			trigger={(triggerProps) => (
				<Tooltip
					content={isPopupOpen ? null : tooltip}
					hideTooltipOnClick
					position={tooltipPosition}
				>
					{getContainer(triggerProps)}
				</Tooltip>
			)}
			content={() =>
				renderPopupComponent ? (
					renderPopupComponent(closePopup)
				) : (
					<Box
						padding="space.250"
						xcss={styles.popup}
						role="dialog"
						aria-labelledby="data-classification-read-content-name"
						aria-describedby="data-classification-read-content-guideline"
						aria-modal="true"
					>
						<ClassificationView
							name={name}
							guideline={guideline}
							guidelineRepresentation={guidelineRepresentation}
						/>
					</Box>
				)
			}
			isOpen={isPopupOpen}
			onClose={() => {
				if (onPopupClose) {
					onPopupClose();
				}
				closePopup();
			}}
			zIndex={popupZIndex}
		/>
	);
}
